import React from "react";
import { ThemeProvider } from "@material-ui/styles";
import { makeStyles } from "@material-ui/core/styles";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import Draggable from "react-draggable";
import Paper from "@material-ui/core/Paper";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import CloseIcon from "@material-ui/icons/Close";
import { Zoom, useMediaQuery } from "@material-ui/core";
import theme from "../../../../Themes/defaultTheme";
import A_IconButton from "../../Inputs/Buttons/A_IconButton";
import "./A_SimpleDialog.css";
import PropTypes from "prop-types";
import { useRef } from "react";

const GLASS_PAPER_STYLE = {
  backgroundColor: "color-mix(in srgb, var(--color-background-paper) 72%, transparent)",
  backgroundImage:
    "linear-gradient(180deg, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0.06) 22%, rgba(255, 255, 255, 0) 46%)",
  border: "1px solid rgba(255, 255, 255, 0.22)",
  boxShadow:
    "0 24px 64px rgba(15, 23, 42, 0.34), inset 0 1px 0 rgba(255, 255, 255, 0.4), inset 0 0 0 1px rgba(255, 255, 255, 0.06)",
  backdropFilter: "blur(40px) saturate(180%)",
  WebkitBackdropFilter: "blur(40px) saturate(180%)",
};

const SOLID_PAPER_BORDER = { border: "1px solid var(--total_row_boldbackground)" };

const GLASS_BACKDROP_STYLE = {
  backgroundColor: "rgba(15, 23, 42, 0.42)",
  backdropFilter: "blur(7px) saturate(120%)",
  WebkitBackdropFilter: "blur(7px) saturate(120%)",
};

const isGlassPaper = (props) => props["data-glass"] === "true";

const PaperComponent = (props) => {
  const glass = isGlassPaper(props);
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="A_DialogContent-root"]'}
      bounds="parent"
    >
      <Paper
        {...props}
        style={{
          margin: 0,
          borderRadius: "calc(16px * var(--app-radius-scale, 1))",
          overflow: "hidden",
          ...(glass ? GLASS_PAPER_STYLE : SOLID_PAPER_BORDER),
        }}
      />
    </Draggable>
  );
};

const MobilePaperComponent = (props) => {
  const glass = isGlassPaper(props);
  return (
    <Paper
      {...props}
      style={{
        margin: 8,
        width: "calc(100% - 16px)",
        maxWidth: "calc(100% - 16px)",
        maxHeight: "calc(100% - 16px)",
        borderRadius: "calc(16px * var(--app-radius-scale, 1))",
        overflow: "hidden",
        ...(glass ? GLASS_PAPER_STYLE : SOLID_PAPER_BORDER),
      }}
    />
  );
};

const A_SimpleDialog = (props) => {
  const divRef = useRef(null);
  const isPlainDialogContent =
    typeof props.dialogContent === "string" ||
    typeof props.dialogContent === "number";
  const useStyles = makeStyles((theme) => ({
    closeButton: {
      position: "absolute",
      right: theme.spacing(1.5),
      top: theme.spacing(1.5),
      color: "var(--color-text-secondary)",
      transition: "color 0.2s ease",
      "&:hover": {
        color: "var(--color-text-primary)",
      },
    },
    ircResize: {
      //  overflowY:"hidden",
      "& section": {
        height: "215px",
      },
    },
    dragIcon: {
      fontSize: "1.125rem",
      color: "var(--color-text-secondary)",
      opacity: 0.6,
    },
    titleText: {
      fontSize: "1.125rem",
      fontWeight: 800,
      color: "var(--color-text-primary)",
      letterSpacing: "-0.01em",
    },
    dialogContent: {
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1, 1.5),
      },
    }
  }));
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const handleOnClose = () => {
    if (divRef.current) {
      divRef.current.scrollTop = 0;
    }
    if (props.onClose) {
      props.onClose();
    }
  };
  const stopPropagationForTab = (event) => {
    if (event.key === "Tab") {
      event.stopPropagation();
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        onKeyDown={stopPropagationForTab}
        showclosebutton={props.showclosebutton}
        onClose={handleOnClose}
        onExit={props.onExit}
        aria-labelledby="simple-dialog-title"
        open={props.open}
        TransitionComponent={Zoom}
        fullScreen={props.fullScreen}
        fullWidth={props.fullWidth}
        maxWidth={props.maxWidth}
        PaperComponent={isMobile ? MobilePaperComponent : PaperComponent}
        PaperProps={
          props.liquidGlass
            ? { "data-glass": "true", className: "a-simple-dialog__paper--glass" }
            : undefined
        }
        BackdropProps={
          props.liquidGlass ? { style: GLASS_BACKDROP_STYLE } : undefined
        }
        disableBackdropClick={props.disableBackdropClick}
        disableEscapeKeyDown={props.disableEscapeKeyDown}
      >
        <DialogTitle>
          <span
            style={{
              cursor: props.title == "" ? "" : "move",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}
            id={
              props.title == ""
                ? "simple-dialog-title"
                : "draggable-dialog-title"
            }
          >
            {props.title !== "" && (
              <DragIndicatorIcon className={classes.dragIcon} />
            )}
            <span className={classes.titleText}>{props.title}</span>
          </span>
          {props.showclosebutton == "true"  && props.condition != true ? (
            <A_IconButton
              icon={<CloseIcon fontSize="small" />}
              aria-label="close"
              className={classes.closeButton}
              onClick={handleOnClose}
            ></A_IconButton>
          ) : null}
          <div>
            <hr style={{ width: "95%", marginBottom: 0, visibility: "hidden" }} />
          </div>
        </DialogTitle>
        <DialogContent
          id="DialogContent"
          className={
            props.formResize
              ? `${classes.dialogContent} ${classes.ircResize}`
              : classes.dialogContent
          }
          ref={divRef}
        >
          {props.IRCdate && (
            <DialogContentText>{props.IRCdate}</DialogContentText>
          )}
          {isPlainDialogContent ? (
            <DialogContentText id="alert-dialog-description">
              {props.dialogContent}
            </DialogContentText>
          ) : (
            <div id="alert-dialog-description">{props.dialogContent}</div>
          )}
        </DialogContent>

        {props.dialogActions && (
          <DialogActions>
            {props.dialogActions}
          </DialogActions>
        )}
      </Dialog>
    </ThemeProvider>
  );
};
A_SimpleDialog.defaultProps = {
  open: false,
  title: "",
  height: "",
  dialogContent: "Dialog content Placeholder",
  fullScreen: false,
  fullWidth: true,
  maxWidth: "xs",
  showclosebutton: "true",
  disableBackdropClick: true,
  disableEscapeKeyDown: false,
  liquidGlass: false,
  //transition: <Slide />,
};
A_SimpleDialog.propTypes = {
  onClose: PropTypes.func,
  onExit: PropTypes.any,
  open: PropTypes.bool,
  maxWidth: PropTypes.string,
  disableBackdropClick: PropTypes.bool,
  disableEscapeKeyDown: PropTypes.bool,
  title: PropTypes.any,
  showclosebutton: PropTypes.any,
  IRCdate: PropTypes.any,
  dialogContent: PropTypes.any,
  dialogActions: PropTypes.any,
  fullScreen: PropTypes.bool,
  fullWidth: PropTypes.bool,
  formResize: PropTypes.bool,
  condition: PropTypes.bool,
  liquidGlass: PropTypes.bool,
};
export default A_SimpleDialog;
